import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ShieldCheck, Truck, Award, Headphones, Star, Quote, Activity } from 'lucide-react';
import { CATEGORIES, PRODUCTS, SERVICES, STATS, TESTIMONIALS, INDUSTRIES } from '../mock/mock';
import ProductCard from '../components/ProductCard';

const FEATURES = [
  { icon: Truck, title: 'Fast Dispatch', text: 'Same-day dispatch on orders before 2pm' },
  { icon: ShieldCheck, title: 'Field Proven', text: 'Gear trusted by health professionals & EMS' },
  { icon: Award, title: 'Certified Training', text: 'Nationally recognised courses' },
  { icon: Headphones, title: 'Expert Support', text: 'Real medics on our support line' },
];

const Home = () => {
  useEffect(() => { window.scrollTo(0, 0); }, []);
  const featured = PRODUCTS.slice(0, 4);

  return (
    <div>
      {/* HERO */}
      <section className="relative bg-[#141414] text-white overflow-hidden">
        <div className="absolute inset-0">
          <img src="https://images.pexels.com/photos/8942043/pexels-photo-8942043.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1600" alt="" className="w-full h-full object-cover opacity-25" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#141414] via-[#141414]/90 to-[#141414]/40" />
        </div>
        <div className="container-fes relative py-24 md:py-32">
          <div className="max-w-2xl animate-fade-up">
            <span className="inline-flex items-center gap-2 section-label bg-white/5 border border-white/10 px-3 py-1.5 rounded-full">
              <Activity size={14} /> Redefining Prehospital Emergency Care
            </span>
            <h1 className="font-head text-4xl md:text-6xl uppercase leading-[1.05] mt-5 mb-5">
              Life-Saving Equipments.<br /><span className="text-[#ef4444]">Elite Prehospital Care Services.</span>
            </h1>
            <p className="text-lg text-gray-300 mb-8 max-w-xl">
              From first aid kits to full event medical coverage and accredited training — FesMed EMS equips and protects when every second counts.
            </p>
            <div className="flex flex-wrap gap-4">
              <Link to="/shop" className="btn-crimson text-base">Products <ArrowRight size={18} /></Link>
              <Link to="/services" className="inline-flex items-center justify-center gap-2 border-2 border-[#15a34a] text-[#15a34a] font-cond font-semibold uppercase tracking-wide px-6 py-3 rounded transition-colors hover:bg-[#15a34a] hover:text-white">Our Services</Link>
            </div>
          </div>
        </div>
        {/* ECG divider */}
        <div className="relative bg-[#c8102e] h-1.5" />
      </section>

      {/* FEATURES */}
      <section className="bg-white border-b border-gray-100">
        <div className="container-fes grid grid-cols-2 lg:grid-cols-4 gap-6 py-10">
          {FEATURES.map((f) => (
            <div key={f.title} className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-lg bg-[#f4f6fa] flex items-center justify-center text-[#c8102e] shrink-0"><f.icon size={24} /></div>
              <div>
                <h4 className="font-cond font-semibold text-[#141414] uppercase text-sm">{f.title}</h4>
                <p className="text-xs text-gray-500">{f.text}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* CATEGORIES */}
      <section className="py-16 md:py-20 bg-[#f4f6fa]">
        <div className="container-fes">
          <div className="text-center mb-12">
            <span className="section-label">Shop by Category</span>
            <h2 className="font-head text-3xl md:text-4xl uppercase text-[#141414] mt-2">Equip for Every Emergency</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {CATEGORIES.map((cat) => (
              <Link key={cat.id} to={`/shop?cat=${cat.id}`} className="group relative rounded-lg overflow-hidden aspect-[4/5] hover-lift">
                <img src={cat.image} alt={cat.name} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#141414] via-[#141414]/40 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-5 text-white">
                  <h3 className="font-head text-xl uppercase">{cat.name}</h3>
                  <p className="text-xs text-gray-200 mb-2">{cat.tagline}</p>
                  <span className="inline-flex items-center gap-1 text-[#ef4444] font-cond uppercase text-sm font-semibold">Shop now <ArrowRight size={14} /></span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* FEATURED PRODUCTS */}
      <section className="py-16 md:py-20 bg-white">
        <div className="container-fes">
          <div className="flex items-end justify-between mb-10">
            <div>
              <span className="section-label">Best Sellers</span>
              <h2 className="font-head text-3xl md:text-4xl uppercase text-[#141414] mt-2">Explore Our Range</h2>
            </div>
            <Link to="/shop" className="hidden md:inline-flex items-center gap-1 font-cond uppercase font-semibold text-[#c8102e] hover:gap-2 transition-all">View all <ArrowRight size={16} /></Link>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-5">
            {featured.map((p) => <ProductCard key={p.id} product={p} />)}
          </div>
        </div>
      </section>

      {/* STATS */}
      <section className="relative py-16 bg-[#141414] text-white overflow-hidden">
        <div className="container-fes grid grid-cols-2 lg:grid-cols-4 gap-8 relative">
          {STATS.map((s) => (
            <div key={s.label} className="text-center">
              <div className="font-head text-4xl md:text-5xl text-[#ef4444]">{s.value}</div>
              <div className="font-cond uppercase tracking-wide text-gray-300 text-sm mt-1">{s.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* SERVICES PREVIEW */}
      <section className="py-16 md:py-20 bg-[#f4f6fa]">
        <div className="container-fes">
          <div className="text-center mb-12">
            <span className="section-label">What We Do</span>
            <h2 className="font-head text-3xl md:text-4xl uppercase text-[#141414] mt-2">Event & Remote Medical Services</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {SERVICES.slice(0, 3).map((s) => (
              <div key={s.id} className="bg-white rounded-lg overflow-hidden hover-lift">
                <div className="aspect-video overflow-hidden">
                  <img src={s.image} alt={s.title} className="w-full h-full object-cover" />
                </div>
                <div className="p-6">
                  <h3 className="font-head text-xl uppercase text-[#141414] mb-2">{s.title}</h3>
                  <p className="text-sm text-gray-600 leading-relaxed">{s.desc}</p>
                </div>
              </div>
            ))}
          </div>
          <div className="text-center mt-10">
            <Link to="/services" className="btn-navy">All Services <ArrowRight size={16} /></Link>
          </div>
        </div>
      </section>

      {/* INDUSTRIES */}
      <section className="py-14 bg-white border-y border-gray-100">
        <div className="container-fes text-center">
          <span className="section-label">Trusted Across Industries</span>
          <div className="flex flex-wrap justify-center gap-3 mt-5">
            {INDUSTRIES.map((ind) => (
              <span key={ind} className="font-cond uppercase tracking-wide text-[#141414] border border-gray-200 rounded-full px-5 py-2 text-sm hover:border-[#c8102e] hover:text-[#c8102e] transition-colors">{ind}</span>
            ))}
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="py-16 md:py-20 bg-[#f4f6fa]">
        <div className="container-fes">
          <div className="text-center mb-12">
            <span className="section-label">Testimonials</span>
            <h2 className="font-head text-3xl md:text-4xl uppercase text-[#141414] mt-2">Trusted by Professionals</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {TESTIMONIALS.map((t) => (
              <div key={t.name} className="bg-white rounded-lg p-7 relative hover-lift">
                <Quote size={36} className="text-[#ef4444]/20 absolute top-5 right-5" />
                <div className="flex gap-1 mb-4">
                  {[...Array(t.rating)].map((_, i) => <Star key={i} size={16} className="fill-[#f5a623] text-[#f5a623]" />)}
                </div>
                <p className="text-gray-700 leading-relaxed mb-5">“{t.text}”</p>
                <div>
                  <div className="font-cond font-semibold text-[#141414] uppercase">{t.name}</div>
                  <div className="text-xs text-gray-400">{t.role}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative py-20 bg-[#141414] text-white overflow-hidden">
        <div className="container-fes text-center relative">
          <h2 className="font-head text-3xl md:text-5xl uppercase mb-4">Ready When You Are</h2>
          <p className="text-white/90 max-w-2xl mx-auto mb-8 text-lg">Request a quote for event medical coverage, or book your team onto an accredited training course today.</p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/contact" className="inline-flex items-center justify-center gap-2 bg-[#15a34a] text-white font-cond font-semibold uppercase tracking-wide px-7 py-3 rounded hover:bg-[#108040] transition-colors">Get a Quote</Link>
            <Link to="/training" className="inline-flex items-center justify-center gap-2 border-2 border-[#15a34a] text-[#15a34a] font-cond font-semibold uppercase tracking-wide px-7 py-3 rounded hover:bg-[#15a34a] hover:text-white transition-colors">Book Training</Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
